import React, { useState, useCallback } from 'react'
import { FormErrorMessage } from 'common/FormErrorMessage/FormErrorMessage'
import { Button } from 'blaze-ui/Button'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UserClient } from 'api/clients/UserClient'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { trackVerifyWithCodeComplete, trackVerifyWithCodeFailed } from 'screens/LoginOrSignup/utils'
import { RegistrationStepProps } from 'screens/Registration/types'
import { CodeInput } from 'screens/Registration/CodeInput/CodeInput'
import Styles from './VerifyEmail.module.scss'
import useURLParams from 'hooks/useURLParams'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { Text } from 'blaze-ui/Text/Text'
import { WhiteLabelLogo } from 'components/WhiteLabelLogo/WhiteLabelLogo'
import { useInvitedWorkspace } from 'hooks/useInvitedWorkspace'

export const VerifyEmail = ({ onNext }: RegistrationStepProps) => {
  const [clearInputKey, setClearInputKey] = useState<number>(0)
  const [error, setError] = useState<string>('')
  const [codeDisabled, setCodeDisabled] = useState<boolean>(false)
  const [searchParams, setSearchParams] = useURLParams({ pushNewState: false })
  const email = searchParams.get('email') ? decodeURIComponent(searchParams.get('email') || '') : ''
  const workspaceInviteToken =
    searchParams.get('workspaceInviteToken') || searchParams.get('workspace_invite_token') || undefined
  const { workspace: invitedWorkspace } = useInvitedWorkspace(workspaceInviteToken)

  const resendEmail = useCallback(async () => {
    const res = await UserClient.sendEmail(email, 'email_verification', undefined, workspaceInviteToken)
    if (res.type === SUCCESS_RESPONSE_TYPE) {
      showToastMessage('Email sent!', 'success')
    } else {
      showToastMessage('Something went wrong. Please try again later', 'error')
    }
  }, [email, workspaceInviteToken])

  const checkVerificationCode = useCallback(
    async (code: string) => {
      setCodeDisabled(true)
      const resp = await UserClient.verifyEmailConfirmationCode(email, code)

      if (resp.type === SUCCESS_RESPONSE_TYPE) {
        setSearchParams({ emailVerificationCode: code })

        trackVerifyWithCodeComplete()
        onNext()
        return
      }

      if (
        resp.type === 'error' &&
        (resp.status === 403 || (resp.data?.errors && resp.data.errors[0]?.verify_with_code_attempts >= 3))
      ) {
        setError('Maximum code attempts have been exceeded. Verify your email by clicking the link we sent to you')
      } else {
        setError('The code you entered does not match our records')
      }

      setCodeDisabled(false)
      trackVerifyWithCodeFailed(error)
      setClearInputKey((prev) => prev + 1)
    },
    [email, error, setSearchParams, onNext],
  )

  const handleCodeChange = useCallback(() => {
    setError('')
  }, [])

  return (
    <MultiStepStepLayout
      title="Verify your email"
      description={
        <div className={Styles.description}>
          <Text>Enter the code we sent to {email} to finish signing up.</Text>
          <Text>
            Check your <b>Spam folder</b> in case you don't see it.
          </Text>
        </div>
      }
      centerContent
      scrollContainerClassName={Styles.scrollContainer}
    >
      <div className={Styles.container}>
        <div className={Styles.logo}>
          <WhiteLabelLogo workspace={invitedWorkspace} width={108} height={30} />
        </div>
        <CodeInput
          disabled={codeDisabled}
          key={clearInputKey}
          onChange={handleCodeChange}
          onComplete={checkVerificationCode}
        />
        {error && (
          <div className="errorText">
            <FormErrorMessage>{error}</FormErrorMessage>
          </div>
        )}
        <div className={Styles.resendEmail}>
          <Button variant="tertiary" size="xl" onPress={resendEmail}>
            Resend Email
          </Button>
        </div>
      </div>
    </MultiStepStepLayout>
  )
}
